/**
 * SetupPage — イベント設定画面 (/setup)
 *
 * Step 1: イベント選択 or 新規作成
 * Step 2: 確認 + 専用ブックマークURL表示
 *
 * ?cfg= パラメータがあれば自動デコードして Step 2 へジャンプ
 *
 * 管理トークンフロー:
 *  - 新規作成: POST /event/create でサーバー側ID+トークンを取得
 *  - 既存イベント（トークンなし）: POST /event/claim で移行取得
 *  - 全リクエストにmanagementTokenを付加
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEventStore, EventConfig } from '../stores/eventStore';
import { t, LangSwitch, useLang, getLang } from '../i18n';

const WORKER_URL = import.meta.env.VITE_WORKER_URL as string ?? 'http://localhost:8787';

interface RegionPrice { currency: 'jpy' | 'usd'; basePrice: number; hourlyPrice: number; taxInclusive: boolean }
interface PricingTable { jp: RegionPrice; intl: RegionPrice }
// /pricing が取れないときの表示用（実際の請求額は Worker が決める）
const DEFAULT_PRICING: PricingTable = {
  jp: { currency: 'jpy', basePrice: 11000, hourlyPrice: 5500, taxInclusive: true },
  intl: { currency: 'usd', basePrice: 79, hourlyPrice: 39, taxInclusive: false },
};

type Step = 'select' | 'confirm';

// ── URL エンコード/デコード ────────────────────────────
interface BookmarkConfig {
  name: string;
  glossary: string[];
}

function encodeConfig(cfg: BookmarkConfig): string {
  return btoa(encodeURIComponent(JSON.stringify(cfg)));
}

function decodeConfig(encoded: string): BookmarkConfig | null {
  try {
    return JSON.parse(decodeURIComponent(atob(encoded)));
  } catch {
    return null;
  }
}

export function generateBookmarkUrl(name: string, glossary: string[]): string {
  const cfg = encodeConfig({ name, glossary });
  return `${window.location.origin}/setup?cfg=${cfg}`;
}

// ──────────────────────────────────────────────────────
export default function SetupPage() {
  useLang();
  // 管理者ボタン：一度 ?admin=1 で開けば、このタブではイベントを選び直しても表示し続ける
  const showAdmin = (() => {
    try {
      if (new URLSearchParams(window.location.search).get('admin') === '1') sessionStorage.setItem('amo_show_admin', '1');
      return sessionStorage.getItem('amo_show_admin') === '1';
    } catch { return new URLSearchParams(window.location.search).get('admin') === '1'; }
  })();
  const navigate = useNavigate();
  const { events, load, upsertEvent, setActiveEvent } = useEventStore();

  const [step, setStep] = useState<Step>('select');
  const [draft, setDraft] = useState<Partial<EventConfig>>({ glossary: [] });
  const [contractedHours, setContractedHours] = useState(2);
  const [copied, setCopied] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // 料金は Worker の /pricing から取得（英語画面は海外価格 USD、日本語画面は日本価格 JPY）
  const [pricing, setPricing] = useState<PricingTable>(DEFAULT_PRICING);
  useEffect(() => {
    fetch(`${WORKER_URL}/pricing`).then((r) => r.json())
      .then((d: PricingTable) => { if (d?.jp && d?.intl) setPricing(d); })
      .catch(() => { /* 既定値のまま */ });
  }, []);
  const price = getLang() === 'en' ? pricing.intl : pricing.jp;
  const money = (n: number) => (price.currency === 'usd' ? `$${n.toLocaleString('en-US')}` : `¥${n.toLocaleString()}`);
  const BASE_FEE = price.basePrice;
  const HOURLY_FEE = price.hourlyPrice;
  const totalFee = BASE_FEE + contractedHours * HOURLY_FEE;

  // ── 既存イベントの管理トークン移行（起動時） ────────────
  const claimMissingTokens = async (eventList: EventConfig[]): Promise<void> => {
    const unclaimed = eventList.filter((e) => !e.managementToken);
    for (const e of unclaimed) {
      try {
        const res = await fetch(`${WORKER_URL}/event/claim`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            eventId: e.id,
            eventName: e.name,
            direction: e.direction,
            glossary: e.glossary,
          }),
        });
        if (res.ok) {
          const data = (await res.json()) as { managementToken?: string };
          if (data.managementToken) {
            upsertEvent({ ...e, managementToken: data.managementToken });
          }
        }
      } catch (err) {
        console.warn('[Andiamo] /event/claim failed for', e.id, err);
      }
    }
  };

  useEffect(() => {
    load();

    const params = new URLSearchParams(window.location.search);
    const cfg = params.get('cfg');
    if (cfg) {
      const decoded = decodeConfig(cfg);
      if (decoded) {
        setDraft({
          // 新規イベントとして扱う（cfg からはIDなし）
          name: decoded.name,
          direction: 'en-ja',
          glossary: decoded.glossary ?? [],
        });
        setStep('confirm');
      }
    }
  }, []);

  // events がロードされたら未claimトークンを取得
  useEffect(() => {
    if (events.length > 0) {
      claimMissingTokens(events).catch((e) => console.warn('[Andiamo] claimMissingTokens error', e));
    }
  }, [events.length]);

  // ── サーバー側でイベントを作成（新規） ─────────────────────
  const createEventOnServer = async (
    eventName: string,
    direction: 'en-ja' | 'ja-en',
    glossary: string[]
  ): Promise<{ eventId: string; managementToken: string } | null> => {
    try {
      const res = await fetch(`${WORKER_URL}/event/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventName, direction, glossary }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { eventId?: string; managementToken?: string };
      if (data.eventId && data.managementToken) {
        return { eventId: data.eventId, managementToken: data.managementToken };
      }
      return null;
    } catch (e) {
      console.warn('[Andiamo] /event/create failed, falling back to local UUID:', e);
      return null;
    }
  };

  // ── 既存イベントをサーバーに同期（用語集更新時） ───────────
  const syncEventToServer = async (event: EventConfig): Promise<void> => {
    try {
      await fetch(`${WORKER_URL}/event/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId: event.id,
          eventName: event.name,
          direction: event.direction,
          glossary: event.glossary,
        }),
      });
    } catch (e) {
      console.warn('[Andiamo] /event/register failed:', e);
    }
  };

  // ── 開始ハンドラ ──────────────────────────────────────
  const handleStart = async (mode: 'test' | 'production' | 'admin') => {
    let event: EventConfig;

    if (draft.id) {
      // 既存イベントの再利用
      event = {
        id: draft.id,
        name: draft.name ?? t('無題イベント', 'Untitled event'),
        direction: draft.direction ?? 'en-ja',
        glossary: draft.glossary ?? [],
        createdAt: Date.now(),
        lastUsedAt: Date.now(),
        managementToken: draft.managementToken,
      };
      // 用語集が変わっている可能性があるのでサーバーに同期
      await syncEventToServer(event);
    } else {
      // 新規イベント: サーバー側でID+トークンを取得
      const serverResult = await createEventOnServer(
        draft.name ?? t('無題イベント', 'Untitled event'),
        draft.direction ?? 'en-ja',
        draft.glossary ?? []
      );

      if (serverResult) {
        event = {
          id: serverResult.eventId,
          name: draft.name ?? t('無題イベント', 'Untitled event'),
          direction: draft.direction ?? 'en-ja',
          glossary: draft.glossary ?? [],
          createdAt: Date.now(),
          lastUsedAt: Date.now(),
          managementToken: serverResult.managementToken,
        };
      } else {
        // サーバー作成失敗 → ローカルUUIDにフォールバック（後でclaimで取得）
        event = {
          id: crypto.randomUUID(),
          name: draft.name ?? t('無題イベント', 'Untitled event'),
          direction: draft.direction ?? 'en-ja',
          glossary: draft.glossary ?? [],
          createdAt: Date.now(),
          lastUsedAt: Date.now(),
        };
        await syncEventToServer(event);
      }
    }

    upsertEvent(event);
    setActiveEvent(event.id);

    if (mode === 'test') {
      navigate(`/operator?mode=test&minutes=15`);
      return;
    }

    if (mode === 'admin') {
      navigate(`/operator?mode=test&minutes=480&admin=1`);
      return;
    }

    // 本番: まず既存セッションの再開を試みる
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      // ① 購入済みセッションの再開チェック
      if (event.managementToken) {
        const resumeRes = await fetch(`${WORKER_URL}/event/start-translation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${event.managementToken}`,
          },
          body: JSON.stringify({ eventId: event.id, eventName: event.name }),
        });
        if (resumeRes.ok) {
          const resumeData = await resumeRes.json() as { sessionToken?: string; sessionId?: string; expiresAt?: number; hardExpiresAt?: number };
          if (resumeData.sessionToken) {
            // 既存セッション復元成功 → 翻訳ページへ直接遷移
            sessionStorage.setItem('andiamo_session_token', resumeData.sessionToken);
            sessionStorage.setItem('andiamo_session_id', resumeData.sessionId ?? '');
            if (resumeData.expiresAt) sessionStorage.setItem('andiamo_expires_at', String(resumeData.expiresAt));
            if (resumeData.hardExpiresAt) sessionStorage.setItem('andiamo_hard_expires_at', String(resumeData.hardExpiresAt));
            setCheckoutLoading(false);
            navigate(`/operator?mode=production&from=payment`);
            return;
          }
        }
      }

      // ② 既存セッションなし → Stripe Checkout へリダイレクト
      const encodedConfig = encodeConfig({ name: event.name, glossary: event.glossary });
      const res = await fetch(`${WORKER_URL}/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId: event.id,
          eventName: event.name,
          purchasedHours: contractedHours,
          encodedConfig,
          managementToken: event.managementToken ?? undefined,
          lang: getLang(),
        }),
      });
      const data = await res.json() as { checkoutUrl?: string; error?: string };
      if (!res.ok || !data.checkoutUrl) {
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
      window.location.href = data.checkoutUrl;
    } catch (e) {
      setCheckoutError(e instanceof Error ? e.message : t('決済エラーが発生しました', 'A payment error occurred'));
      setCheckoutLoading(false);
    }
  };

  // ── ブックマークURL ───────────────────────────────────
  const bookmarkUrl = generateBookmarkUrl(draft.name ?? '', draft.glossary ?? []);

  const handleCopy = () => {
    navigator.clipboard.writeText(bookmarkUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Step 1: イベント選択 ──────────────────────────────
  if (step === 'select') {
    const sorted = [...events]
      .sort((a, b) => b.lastUsedAt - a.lastUsedAt)
      .filter((e, idx, arr) => arr.findIndex((x) => x.name === e.name) === idx);
    return (
      <div className="setup-page">
        <div className="setup-card">
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><LangSwitch /></div>
          <h1 className="setup-title">🌐 Amo Live Translator</h1>
          <h2 className="setup-subtitle">{t('イベントを選択または新規作成', 'Choose an event or create a new one')}</h2>

          {sorted.length > 0 && (
            <div className="event-list">
              <p className="event-list-label">{t('過去のイベント（クリックで再利用）', 'Previous events (click to reuse)')}</p>
              {sorted.map((e) => (
                <button
                  key={e.id}
                  className="event-item"
                  onClick={() => {
                    setDraft({
                      id: e.id,
                      name: e.name,
                      direction: e.direction,
                      glossary: [...e.glossary],
                      managementToken: e.managementToken,
                    });
                    setStep('confirm');
                  }}
                >
                  <span className="event-name">{e.name}</span>
                  <span className="event-meta">
                    {t(`用語${e.glossary.length}件`, `${e.glossary.length} terms`)} ·{' '}
                    {new Date(e.lastUsedAt).toLocaleDateString('ja-JP')}
                    {e.managementToken ? '' : ' ⚠️'}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="new-event-form">
            <p className="event-list-label">{t('新規イベント', 'New event')}</p>
            <input
              type="text"
              className="input-text"
              placeholder={t('イベント名（例：2026年秋季セミナー）', 'Event name (e.g. Autumn Seminar 2026)')}
              value={draft.name ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && draft.name?.trim()) {
                  setStep('confirm');
                }
              }}
              autoFocus
            />
            <button
              className="btn btn-primary btn-xl"
              disabled={!draft.name?.trim()}
              onClick={() => setStep('confirm')}
            >
              {t('次へ →', 'Next →')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2: 確認 + 専用URL ──────────────────────────
  return (
    <div className="setup-page">
      <div className="setup-card">
        <button className="setup-back" onClick={() => setStep('select')}>{t('← 戻る', '← Back')}</button>
        <h2 className="setup-subtitle">{t('開始確認', 'Confirm and start')}</h2>

        <div className="confirm-summary">
          <div className="confirm-row">
            <span className="confirm-label">{t('イベント名', 'Event name')}</span>
            <span className="confirm-value">{draft.name}</span>
          </div>
          <div className="confirm-row">
            <span className="confirm-label">{t('用語集', 'Glossary')}</span>
            <span className="confirm-value">
              {(draft.glossary?.length ?? 0) > 0
                ? t(`${draft.glossary!.length}件（翻訳画面で編集可）`, `${draft.glossary!.length} terms (editable on the operator screen)`)
                : t('未登録（翻訳画面で追加できます）', 'None (you can add terms on the operator screen)')}
            </span>
          </div>
        </div>

        {/* 契約時間と料金 */}
        <div className="hours-selector">
          <p className="event-list-label">{t('本番の契約時間', 'Hours to purchase')}</p>
          <div className="hours-btns">
            {[1,2,3,4,5,6,7,8,9,10,11,12].map((h) => (
              <button
                key={h}
                className={`hours-btn ${contractedHours === h ? 'selected' : ''}`}
                onClick={() => setContractedHours(h)}
              >
                {h}h
              </button>
            ))}
          </div>
          <div className="price-breakdown">
            <div className="price-row">
              <span>{t('基本利用料', 'Base fee')}</span>
              <span>{money(BASE_FEE)}</span>
            </div>
            <div className="price-row">
              <span>{t('1時間利用料', 'Hourly fee')}（{contractedHours}h × {money(HOURLY_FEE)}）</span>
              <span>{money(contractedHours * HOURLY_FEE)}</span>
            </div>
            <div className="price-row price-total">
              <span>{price.taxInclusive ? t('合計（税込）', 'Total (tax incl.)') : t('合計（米ドル）', 'Total (USD)')}</span>
              <span>{money(totalFee)}</span>
            </div>
          </div>
        </div>

        {/* 専用ブックマークURL */}
        <div className="bookmark-box">
          <p className="bookmark-label">{t('📌 次回用の専用URLを保存してください', '📌 Save this event URL for next time')}</p>
          <p className="bookmark-hint">
            {t('このURLを開くだけで用語集が自動復元されます', 'Opening this URL restores your glossary automatically')}
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-primary bookmark-copy-btn" style={{ flex: 1 }} onClick={handleCopy}>
              {copied ? t('✅ URLをコピーしました', '✅ URL copied') : t('🔗 URLをコピー', '🔗 Copy URL')}
            </button>
          </div>
          <p className="bookmark-kbd">{t('コピー後 Cmd+D（Mac）/ Ctrl+D（Windows）でブックマーク登録', 'After copying, press Cmd+D (Mac) / Ctrl+D (Windows) to bookmark it')}</p>
        </div>

        {checkoutError && (
          <div style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
            ⚠️ {checkoutError}
          </div>
        )}

        <div className="confirm-actions">
          <button
            className="btn btn-ghost btn-lg confirm-btn-test"
            onClick={() => handleStart('test')}
            disabled={checkoutLoading}
          >
            {t('🔧 接続テスト（15分・無料）', '🔧 Connection test (15 min, free)')}
            <span className="confirm-btn-note">{t('動作確認用・課金なし', 'For checking your setup. No charge')}</span>
            <span className="confirm-btn-note">{t('1日3回まで。再読み込みしても続きから再開します', 'Up to 3 per day. Reloading resumes the same test')}</span>
          </button>

          {showAdmin && (
            <button
              className="btn btn-ghost btn-lg confirm-btn-test"
              onClick={() => {
                let secret: string | null = null;
                try { secret = sessionStorage.getItem('amo_admin_secret'); } catch { /* ignore */ }
                if (!secret) {
                  secret = window.prompt(t('管理者パスワードを入力してください', 'Enter the admin password'))?.trim() || null;
                  if (!secret) return;
                  try { sessionStorage.setItem('amo_admin_secret', secret); } catch { /* ignore */ }
                }
                handleStart('admin');
              }}
              disabled={checkoutLoading}
              style={{ borderColor: '#f59e0b', color: '#f59e0b' }}
            >
              {t('🔑 管理者モード（8時間・無制限）', '🔑 Admin mode (8 hours)')}
              <span className="confirm-btn-note">{t('社内テスト用', 'Internal testing')}</span>
            </button>
          )}

          <button
            className="btn btn-success btn-xl confirm-btn-production"
            onClick={() => handleStart('production')}
            disabled={checkoutLoading}
          >
            {checkoutLoading
              ? t('⏳ Stripeへ接続中...', '⏳ Connecting to Stripe...')
              : t(`▶ 本番開始（${contractedHours}時間・${money(totalFee)}）`, `▶ Go live (${contractedHours} h · ${money(totalFee)})`)}
            {!checkoutLoading && <span className="confirm-btn-note">{t('カード決済 → 翻訳開始', 'Pay by card → start translating')}</span>}
          </button>
        </div>
      </div>
    </div>
  );
}
